import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  User, FileText, Calendar, CheckCircle, Clock, Target, 
  AlertTriangle, ChevronDown, ChevronRight, Download, 
  Heart, Home, Briefcase, Users, Scale, Brain, Pill,
  Shield, MapPin, Gavel, Activity, BarChart3, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import { sanitizeHtml } from '../lib/sanitize';
import { PROGRAMME_WEEK_COUNT, isTwoWeekWindow, programmeWeekNumber } from '../lib/weekLabels';
import useAllowPrint from '../components/useAllowPrint';

const STICKMAN_IMAGE = 'https://customer-assets.emergentagent.com/job_c2b961a8-ea8c-4dbb-ad47-155fcf8e5075/artifacts/tot5eq2a_Stone2%20Stickman1.jpeg';

// Life area colour coding for the stickman
const LIFE_AREAS = [
  { id: 'health_diet', label: 'Health & Diet', icon: Heart, color: 'red', bgClass: 'bg-red-50 border-red-200', textClass: 'text-red-700', badgeClass: 'bg-red-100 text-red-800' },
  { id: 'job_finances', label: 'Job & Finances', icon: Briefcase, color: 'amber', bgClass: 'bg-amber-50 border-amber-200', textClass: 'text-amber-700', badgeClass: 'bg-amber-100 text-amber-800' },
  { id: 'friends', label: 'Friends', icon: Users, color: 'green', bgClass: 'bg-green-50 border-green-200', textClass: 'text-green-700', badgeClass: 'bg-green-100 text-green-800' },
  { id: 'home', label: 'Home', icon: Home, color: 'blue', bgClass: 'bg-blue-50 border-blue-200', textClass: 'text-blue-700', badgeClass: 'bg-blue-100 text-blue-800' },
  { id: 'relationships', label: 'Relationships', icon: Heart, color: 'purple', bgClass: 'bg-purple-50 border-purple-200', textClass: 'text-purple-700', badgeClass: 'bg-purple-100 text-purple-800' },
  { id: 'family', label: 'Family', icon: Users, color: 'indigo', bgClass: 'bg-indigo-50 border-indigo-200', textClass: 'text-indigo-700', badgeClass: 'bg-indigo-100 text-indigo-800' },
];

function Section({ id, icon: Icon, title, children, defaultOpen = false, color = 'blue', badge, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-zinc-50 transition-colors"
        data-testid={testId}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg bg-${color}-50 flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 text-${color}-600`} />
          </div>
          <span className="text-sm font-semibold text-zinc-900">{title}</span>
          {badge && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge.className || 'bg-zinc-100 text-zinc-600'}`}>{badge.text}</span>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && <div className="border-t border-zinc-100 px-4 py-4">{children}</div>}
    </div>
  );
}

function DataRow({ label, value, testId }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-zinc-50 last:border-0" data-testid={testId}>
      <span className="text-xs text-zinc-500 flex-shrink-0 w-2/5">{label}</span>
      <span className="text-xs text-zinc-900 font-medium text-right flex-1">{value}</span>
    </div>
  );
}

function StatusBadge({ completed, label }) {
  return completed ? (
    <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
      <CheckCircle className="w-3 h-3" /> {label || 'Complete'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
      <Clock className="w-3 h-3" /> {label || 'Incomplete'}
    </span>
  );
}

// Interactive stickman that populates from personal history + stickman assessment
function InteractiveStickman({ personalHistory, stickmanData, userName, onNavigate }) {
  const [activeArea, setActiveArea] = useState(null);

  const getAreaContent = (areaId) => {
    const ph = personalHistory || {};
    const sd = stickmanData || {};
    
    switch(areaId) {
      case 'health_diet': {
        const items = [];
        if (ph.primary_drug) items.push({ label: 'Primary Drug', value: ph.primary_drug });
        if (ph.secondary_drugs?.length) items.push({ label: 'Secondary', value: ph.secondary_drugs.join(', ') });
        if (ph.method_of_use?.length) items.push({ label: 'Method', value: ph.method_of_use.join(', ') });
        if (ph.ever_injected) items.push({ label: 'Injected', value: ph.ever_injected });
        if (ph.has_mental_health_diagnosis) items.push({ label: 'MH Diagnosis', value: ph.has_mental_health_diagnosis });
        if (sd.health_diet) items.push({ label: 'Self-Assessment', value: sd.health_diet });
        return items;
      }
      case 'job_finances': {
        const items = [];
        if (ph.is_employed) items.push({ label: 'Employed', value: ph.is_employed });
        if (ph.has_previous_employment) items.push({ label: 'Prev. Employed', value: ph.has_previous_employment });
        if (ph.employer) items.push({ label: 'Employer', value: ph.employer });
        if (ph.profession) items.push({ label: 'Profession', value: ph.profession });
        if (ph.industry) items.push({ label: 'Industry', value: ph.industry });
        if (ph.income_source) items.push({ label: 'Income', value: ph.income_source });
        if (ph.income_details) items.push({ label: 'Details', value: ph.income_details });
        if (sd.job_finances) items.push({ label: 'Self-Assessment', value: sd.job_finances });
        return items;
      }
      case 'friends': {
        const items = [];
        if (sd.friends) items.push({ label: 'Self-Assessment', value: sd.friends });
        return items;
      }
      case 'home': {
        const items = [];
        if (ph.living_arrangement) items.push({ label: 'Living', value: ph.living_arrangement });
        if (ph.usual_accommodation) items.push({ label: 'Accommodation', value: ph.usual_accommodation });
        if (ph.homeless_details) items.push({ label: 'Details', value: ph.homeless_details });
        if (sd.home) items.push({ label: 'Self-Assessment', value: sd.home });
        return items;
      }
      case 'relationships': {
        const items = [];
        if (ph.has_legal_matters) items.push({ label: 'Legal Matters', value: ph.has_legal_matters });
        if (ph.charge_types?.length) items.push({ label: 'Charges', value: ph.charge_types.join(', ') });
        if (ph.currently_on_orders) items.push({ label: 'On Orders', value: ph.currently_on_orders });
        if (sd.relationships) items.push({ label: 'Self-Assessment', value: sd.relationships });
        return items;
      }
      case 'family': {
        const items = [];
        if (ph.has_previous_treatment) items.push({ label: 'Prev. Treatment', value: ph.has_previous_treatment });
        if (ph.treatment_type) items.push({ label: 'Treatment Type', value: ph.treatment_type });
        if (ph.indigenous_status) items.push({ label: 'Indigenous', value: ph.indigenous_status });
        if (sd.family) items.push({ label: 'Self-Assessment', value: sd.family });
        return items;
      }
      default: return [];
    }
  };

  const getAreaStatus = (areaId) => {
    const content = getAreaContent(areaId);
    if (content.length === 0) return 'empty';
    if (content.some(i => i.label === 'Self-Assessment')) return 'complete';
    return 'partial';
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="interactive-stickman">
      {/* Stickman image with tap areas */}
      <div className="relative mx-auto max-w-xs mb-4">
        <img src={STICKMAN_IMAGE} alt="Stickman Assessment" className="w-full max-h-64 object-contain" />
        {userName && (
          <div className="absolute bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md" style={{ top: '18%', left: '50%', transform: 'translateX(-50%)' }}>
            {userName}
          </div>
        )}
      </div>

      {/* Life area cards */}
      <div className="grid grid-cols-2 gap-2">
        {LIFE_AREAS.map((area) => {
          const status = getAreaStatus(area.id);
          const content = getAreaContent(area.id);
          const isActive = activeArea === area.id;
          const Icon = area.icon;
          
          return (
            <button
              key={area.id}
              onClick={() => setActiveArea(isActive ? null : area.id)}
              className={`text-left rounded-lg border p-2.5 transition-all ${
                isActive ? `${area.bgClass} ring-2 ring-${area.color}-400` : 
                status === 'complete' ? `${area.bgClass}` :
                status === 'partial' ? 'bg-amber-50/50 border-amber-100' :
                'bg-zinc-50 border-zinc-200'
              }`}
              data-testid={`stickman-area-${area.id}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className={`w-3 h-3 ${area.textClass}`} />
                <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-700">{area.label}</span>
              </div>
              {status === 'empty' ? (
                <span className="text-[10px] text-zinc-400 italic">Not recorded</span>
              ) : (
                <span className={`text-[10px] font-medium ${area.textClass}`}>
                  {content.length} item{content.length !== 1 ? 's' : ''} recorded
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Expanded detail panel */}
      {activeArea && (
        <div className={`mt-3 rounded-lg border p-3 ${LIFE_AREAS.find(a => a.id === activeArea)?.bgClass || 'bg-zinc-50 border-zinc-200'}`} data-testid="stickman-detail-panel">
          <h4 className="text-xs font-bold text-zinc-800 uppercase mb-2">
            {LIFE_AREAS.find(a => a.id === activeArea)?.label} - Details
          </h4>
          {getAreaContent(activeArea).length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No data recorded yet. Complete your Personal History and Stickman Assessment.</p>
          ) : (
            <div className="space-y-1">
              {getAreaContent(activeArea).map((item, i) => (
                <div key={i} className="flex justify-between py-1 border-b border-white/50 last:border-0">
                  <span className="text-[10px] text-zinc-500">{item.label}</span>
                  <span className="text-[10px] text-zinc-900 font-medium text-right max-w-[60%]">{item.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-3">
        <Button
          onClick={() => onNavigate('/self-work/stickman')}
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          data-testid="edit-stickman-btn"
        >
          Edit Stickman
        </Button>
        <Button
          onClick={() => onNavigate('/personal-history')}
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          data-testid="edit-history-btn"
        >
          Edit History
        </Button>
      </div>
    </div>
  );
}


// Court Report - formal prose document for CJS sentencing
function CourtReport({ data, onClose, historyProgress, historyComplete, stickmanFilled, stickmanComplete }) {
  const ph = data?.personal_history || {};
  const sd = data?.stickman || {};
  const ui = data?.user_info || {};
  const pr = data?.progress || {};
  const cp = data?.case_plan || {};
  const reportRef = useRef(null);
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  const enrolled = ui.created_at ? new Date(ui.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';
  const sobrietyDays = ui.sobriety_days || 0;

  const pronoun = ph.gender === 'female' ? 'she' : ph.gender === 'male' ? 'he' : 'they';
  const possessive = ph.gender === 'female' ? 'her' : ph.gender === 'male' ? 'his' : 'their';
  const Pronoun = pronoun.charAt(0).toUpperCase() + pronoun.slice(1);
  const honorific = ph.gender === 'female' ? 'Ms' : ph.gender === 'male' ? 'Mr' : 'Mx';
  const lastName = (ui.name || '').split(' ').slice(-1)[0] || 'the participant';
  const fullRef = `${honorific} ${lastName}`;
  const hasVerb = pronoun === 'they' ? 'have' : 'has';
  const isVerb = pronoun === 'they' ? 'are' : 'is';

  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;
    // Build the print document DOM-safely without document.write().  We
    // create a Blob URL that serves a sanitised version of the report body
    // wrapped in the print stylesheet, then open it in a new tab.  Using
    // DOMPurify means even if the report body somehow contains user-
    // controlled markup (it shouldn't) we cannot execute scripts.
    const safeBody = sanitizeHtml(printContent.innerHTML);
    const html = `
      <html><head><title>Axiom ARP - Court Report - ${(ui.name || 'Client').replace(/[<>&"']/g, '')}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Times New Roman', serif; font-size: 11pt; color: #111; line-height: 1.7; padding: 40px 50px; }
        h1 { font-size: 14pt; text-align: center; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 2px; }
        h2 { font-size: 11pt; margin-top: 18px; margin-bottom: 8px; border-bottom: 1px solid #333; padding-bottom: 3px; text-transform: uppercase; letter-spacing: 1px; }
        p { margin-bottom: 10px; text-align: justify; }
        .subtitle { text-align: center; font-size: 10pt; color: #555; margin-bottom: 4px; }
        .confidential { text-align: center; font-size: 9pt; color: #c00; font-weight: bold; margin-bottom: 16px; }
        .disclaimer { background: #f5f5f5; border: 1px solid #ccc; padding: 12px; margin-top: 20px; font-size: 9pt; line-height: 1.5; }
        .header-line { border-top: 2px solid #333; border-bottom: 2px solid #333; padding: 10px 0; text-align: center; margin-bottom: 14px; }
        .signature-block { margin-top: 40px; display: flex; justify-content: space-between; }
        .sig-line { border-top: 1px solid #333; width: 200px; text-align: center; padding-top: 4px; font-size: 9pt; }
        .watermark { text-align: center; font-size: 8pt; color: #999; margin-top: 30px; border-top: 1px solid #ddd; padding-top: 8px; }
        table { width: 100%; border-collapse: collapse; margin: 8px 0; }
        td, th { border: 1px solid #ccc; padding: 4px 8px; text-align: left; font-size: 10pt; }
        th { background: #f0f0f0; font-weight: bold; }
        .section-num { display: inline; }
        @media print { body { padding: 20px 30px; } .no-print { display: none; } }
      </style></head><body>${safeBody}</body></html>
    `;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (win) {
      win.addEventListener('load', () => {
        try { win.print(); } catch {}
        // Revoke the URL after a short delay so the print dialog has time to
        // settle.
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      });
    } else {
      URL.revokeObjectURL(url);
      toast.error('Pop-up blocked.  Allow pop-ups for this site to print.');
    }
  };

  const P = ({ children }) => <p className="text-[11px] text-zinc-800 leading-[1.7] mb-3 text-justify">{children}</p>;
  const SectionTitle = ({ children }) => (
    <h2 className="text-xs font-black text-zinc-900 uppercase tracking-wider mt-6 mb-3 pb-1 border-b-2 border-zinc-900">{children}</h2>
  );

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto" data-testid="court-report-overlay">
      <div className="bg-white w-full max-w-2xl my-4 mx-4 rounded-xl shadow-2xl overflow-hidden">
        {/* Toolbar */}
        <div className="sticky top-0 bg-zinc-900 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-sm font-bold text-white">Court Sentencing Report</h3>
          <div className="flex items-center gap-2">
            <Button onClick={handlePrint} size="sm" className="bg-white text-zinc-900 hover:bg-zinc-100 text-xs h-8" data-testid="print-report-btn">
              <Printer className="w-3 h-3 mr-1" /> Print / Save PDF
            </Button>
            <button onClick={onClose} className="text-white/70 hover:text-white text-lg px-2" data-testid="close-report-btn">&times;</button>
          </div>
        </div>

        {/* Report Content */}
        <div ref={reportRef} className="px-8 py-8" style={{ fontFamily: "'Times New Roman', serif" }}>
          {/* Header */}
          <div className="text-center border-y-2 border-zinc-900 py-4 mb-4">
            <h1 className="text-sm font-black uppercase tracking-[0.2em] text-zinc-900">Axiom Recovery Programme</h1>
            <p className="text-[10px] text-zinc-600 mt-1">A Product of The Silkworth Institute</p>
            <p className="text-[10px] text-zinc-700 font-bold mt-2 uppercase tracking-wider">Participant Progress & Compliance Report</p>
            <p className="text-[10px] text-zinc-600">Prepared for Criminal Justice System Use</p>
          </div>
          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
            <span>Report Date: {today}</span>
            <span>Report Reference: ARP-{(ui.name || '').replace(/\s/g, '').substring(0, 6).toUpperCase()}-{new Date().getFullYear()}</span>
          </div>
          <p className="text-[10px] text-red-700 font-bold text-center mb-5">CONFIDENTIAL: FOR AUTHORISED COURT AND LEGAL USE ONLY</p>

          {/* 1. Participant Identification */}
          <SectionTitle>1. Participant Identification</SectionTitle>
          <P>This report pertains to {ui.name || 'the participant'} ({ph.gender ? `${ph.gender}, ` : ''}{ph.age_group || 'age not recorded'}{ph.country_of_birth ? `, born in ${ph.country_of_birth}` : ''}), who enrolled in the Axiom Recovery Programme on {enrolled}. {fullRef} {hasVerb} been assigned to the programme's structured curriculum: a 32-week programme that starts with the 2-Week Window of Personal History, and, as at the date of this report, {isVerb} in {ui.current_week <= 0 ? 'the initial 2-Week Window' : `Programme Week ${ui.current_week} of 30`}.</P>
          <P>{fullRef} nominated {possessive} sobriety/clean date as {ui.sobriety_date ? new Date(ui.sobriety_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'not yet recorded'}, representing {sobrietyDays} days of continuous abstinence as at the date of this report.</P>

          {/* 2. Presenting Crisis */}
          <SectionTitle>2. Presenting Crisis & Circumstances of Referral</SectionTitle>
          {ph.crisis_why_now ? (
            <P>{fullRef} presented to the Axiom Recovery Programme under the following circumstances, as reported in {possessive} own words during the intake assessment: "{ph.crisis_why_now}"</P>
          ) : (
            <P>{fullRef} was referred to the programme for treatment of substance dependence. Specific presenting circumstances were not recorded at the time of intake.</P>
          )}
          {ph.crisis_what_happened && <P>In a more detailed account, {fullRef} stated: "{ph.crisis_what_happened}"</P>}
          {ph.crisis_why_not_sooner && <P>When asked why {pronoun} had not sought treatment sooner, {fullRef} responded: "{ph.crisis_why_not_sooner}"</P>}
          {ph.crisis_heard_about && <P>Source of referral: {ph.crisis_heard_about}.</P>}
          {ph.crisis_what_made_you_stop && <P>When asked what ultimately drove the decision to seek help, {fullRef} stated: "{ph.crisis_what_made_you_stop}"</P>}

          {/* 3. Substance Use Profile */}
          <SectionTitle>3. Substance Use Profile</SectionTitle>
          <P>{fullRef}'s primary substance of dependence is {ph.primary_drug || 'not specified'}{ph.secondary_drugs?.length ? `, with concurrent use of ${ph.secondary_drugs.join(' and ')}` : ''}. {Pronoun} {hasVerb} reported {possessive} method of use as {(ph.method_of_use || []).join(', ') || 'not specified'}. {ph.ever_injected === 'yes' ? `${fullRef} ${hasVerb} disclosed a history of intravenous drug use${ph.injected_last_3_months === 'yes' ? ', including within the most recent three-month period prior to intake' : ''}.` : `${fullRef} denies any history of intravenous drug use.`}</P>
          {ph.additional_drug_info && <P>Additional information regarding substance use: {ph.additional_drug_info}</P>}

          {/* 4. Living Situation & Employment */}
          <SectionTitle>4. Living Situation, Employment & Financial Circumstances</SectionTitle>
          <P>{fullRef}'s current living arrangement is recorded as: {ph.living_arrangement || 'not specified'}{ph.usual_accommodation ? ` (${ph.usual_accommodation})` : ''}.{ph.living_how_long ? ` ${Pronoun} ${hasVerb} resided at this location for ${ph.living_how_long}.` : ''}{ph.living_how_long_stay ? ` ${fullRef} estimates ${pronoun} can remain at this accommodation for ${ph.living_how_long_stay}.` : ''}</P>
          {ph.is_employed === 'yes' ? (
            <P>{fullRef} {isVerb} currently employed as {ph.profession || 'role not specified'}{ph.employer ? ` at ${ph.employer}` : ''}{ph.industry ? ` in the ${ph.industry} industry` : ''}{ph.years_in_industry ? `, with ${ph.years_in_industry} of experience in the sector` : ''}.{ph.work_environment_dangerous === 'yes' ? ` It is noted that ${possessive} current work environment has been assessed as presenting a risk to ${possessive} Recovery: ${ph.work_environment_details || 'details not specified'}.` : ''}</P>
          ) : (
            <P>{fullRef} {isVerb} currently unemployed.{ph.profession ? ` ${possessive} most recent profession was ${ph.profession}` : ''}{ph.industry ? ` in the ${ph.industry} industry` : ''}{ph.years_in_industry ? `, with ${ph.years_in_industry} of experience` : ''}.{ph.lost_business_due_to_addiction === 'yes' ? ` ${fullRef} reports having lost a business as a direct consequence of ${possessive} substance dependence.` : ''}{ph.income_details ? ` Current income: ${ph.income_details}.` : ''}</P>
          )}
          {ph.employment_history?.length > 0 && (
            <>
              <P>Employment history ({ph.employment_history.length} previous position{ph.employment_history.length > 1 ? 's' : ''} recorded):</P>
              {ph.employment_history.map((job, i) => (
                <p key={i} className="text-[10px] text-zinc-700 ml-4 mb-1">&bull; {job.employer || 'N/A'}: {job.profession || 'N/A'} ({job.duration || 'duration not recorded'})</p>
              ))}
            </>
          )}

          {/* 5. Previous Treatment History */}
          <SectionTitle>5. Previous Treatment History</SectionTitle>
          {ph.has_previous_treatment === 'yes' && ph.treatment_entries?.length > 0 ? (
            <>
              <P>{fullRef} reports {ph.treatment_entries.length} previous episode{ph.treatment_entries.length > 1 ? 's' : ''} of treatment for substance dependence:</P>
              {ph.treatment_entries.map((t, i) => (
                <P key={i}><strong>{i === 0 ? 'Most recent' : `Episode ${i + 1}`}:</strong> {t.type || 'Treatment type not specified'} at {t.location || 'location not recorded'}{t.when ? ` (${t.when})` : ''}{t.duration ? `, duration ${t.duration}` : ''}.{t.outcome ? ` Reported outcome: ${t.outcome}.` : ''}</P>
              ))}
            </>
          ) : (
            <P>{fullRef} reports no prior formal treatment for substance dependence prior to enrolling in the Axiom Recovery Programme.</P>
          )}

          {/* 6. Mental Health */}
          <SectionTitle>6. Mental Health</SectionTitle>
          {ph.has_mental_health_diagnosis === 'yes' ? (
            <>
              <P>{fullRef} {hasVerb} disclosed a diagnosed mental health condition{ph.mh_admissions?.[0]?.diagnosis ? `: ${ph.mh_admissions[0].diagnosis}` : ''}. {ph.currently_receiving_mh_treatment === 'yes' ? `${Pronoun} ${isVerb} currently receiving treatment${ph.mh_admissions?.[0]?.location ? ` from ${ph.mh_admissions[0].location}` : ''}.` : `${Pronoun} ${isVerb} not currently receiving psychiatric treatment.`}</P>
              {ph.medication_details && <P>Current psychiatric medications: {ph.medication_details}.</P>}
              {ph.mh_admissions?.length > 1 && (
                <P>{fullRef} reports {ph.mh_admissions.length} mental health treatment episode{ph.mh_admissions.length > 1 ? 's' : ''} in total{ph.mh_admissions.map((a, i) => i > 0 ? `, including ${a.type || 'treatment'} at ${a.location || 'N/A'} (${a.when || 'date not specified'})` : '').join('')}.</P>
              )}
            </>
          ) : (
            <P>{fullRef} {hasVerb} not disclosed a formal mental health diagnosis at the time of this report.</P>
          )}

          {/* 7. Health Concerns */}
          {(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes' || ph.diet_diabetic === 'yes') && (
            <>
              <SectionTitle>7. Significant Health Concerns</SectionTitle>
              {ph.has_major_health_issues === 'yes' && <P>Reported health issues: {ph.health_issue_details || 'details not specified'}.</P>}
              {ph.chronic_conditions === 'yes' && <P>Chronic conditions under treatment: {ph.chronic_condition_details || 'details not specified'}.</P>}
              {ph.medication_allergies === 'yes' && <P>Known medication allergies: {ph.medication_allergy_details || 'present but not specified'}.</P>}
              {ph.diet_diabetic === 'yes' && <P>Diabetes: {ph.diet_diabetic_type === 'type1' ? 'Type 1' : 'Type 2'}, managed via {ph.diet_diabetic_management === 'insulin' ? 'insulin' : ph.diet_diabetic_management === 'dietary' ? 'dietary control' : 'oral medication (tablets)'}.</P>}
              {ph.diet_food_allergies === 'yes' && <P>Food allergies: {ph.diet_allergy_details || 'present'}. Emergency protocol: {ph.diet_allergy_action || 'seek immediate medical attention'}.</P>}
            </>
          )}

          {/* 8. Legal Matters */}
          <SectionTitle>{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '8' : '7'}. Legal Matters & Criminal Justice</SectionTitle>
          {(ph.has_legal_matters === 'yes' || ph.crisis_legal_issue === 'yes') ? (
            <>
              <P>{fullRef} {hasVerb} disclosed current legal proceedings.{ph.charge_types?.length > 0 ? ` Current charge(s): ${ph.charge_types.join(', ')}.` : ''}{ph.legal_location ? ` Court: ${ph.legal_location}.` : ''}{ph.court_date_pending === 'yes' ? ' A court date is currently pending.' : ''}</P>
              {(ph.crisis_using_for_court === 'yes' || ph.axiom_assist_court === 'yes') && (
                <P>{fullRef} {hasVerb} indicated {possessive} intention to use participation in the Axiom Recovery Programme as evidence of rehabilitation for the purposes of sentencing submissions. The Axiom Recovery Programme does not advocate for or against any particular sentencing outcome. This report is provided to offer the Court a factual and verifiable account of {fullRef}'s engagement with a structured, evidence-based Recovery programme.</P>
              )}
              {ph.previous_legal_history === 'yes' && ph.previous_charges?.length > 0 && (
                <>
                  <P>Prior legal history ({ph.previous_charges.length} matter{ph.previous_charges.length > 1 ? 's' : ''} disclosed):</P>
                  {ph.previous_charges.map((c, i) => (
                    <p key={i} className="text-[10px] text-zinc-700 ml-4 mb-1">&bull; {c.when || 'Date unknown'}: {c.charge_type || 'type not specified'}{c.details ? `: ${c.details}` : ''}</p>
                  ))}
                </>
              )}
              {ph.additional_legal_info && <P>{ph.additional_legal_info}</P>}
            </>
          ) : (
            <P>{fullRef} {hasVerb} not disclosed any current or pending legal matters.</P>
          )}

          {/* 9. Programme Compliance */}
          <SectionTitle>{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '9' : '8'}. Programme Compliance & Engagement</SectionTitle>
          <P>Since enrolling on {enrolled}, {fullRef} {hasVerb} demonstrated the following level of engagement with the Axiom Recovery Programme. The programme requires weekly meeting attendance (minimum 5), daily journaling, mental health check-ins, completion of assigned readings (minimum 30 minutes per session), structured lessons and self-work assignments.</P>
          <table className="w-full border-collapse text-[10px] my-2">
            <thead>
              <tr className="bg-zinc-100">
                <th className="border border-zinc-300 px-3 py-1.5 text-left font-bold">Compliance Metric</th>
                <th className="border border-zinc-300 px-3 py-1.5 text-left font-bold">Recorded Count</th>
                <th className="border border-zinc-300 px-3 py-1.5 text-left font-bold">Assessment</th>
              </tr>
            </thead>
            <tbody>
              {[
                { metric: 'Meeting Attendance (verified)', count: pr.meetings_count || 0, assess: (pr.meetings_count || 0) >= 5 ? 'Compliant' : 'Below requirement' },
                { metric: 'Daily Journal Entries', count: pr.journals_count || 0, assess: (pr.journals_count || 0) >= 5 ? 'Active engagement' : 'Developing' },
                { metric: 'Mental Health Check-ins', count: pr.mental_health_count || 0, assess: (pr.mental_health_count || 0) >= 5 ? 'Active engagement' : 'Developing' },
                { metric: 'Readings Completed (30min+)', count: pr.readings_completed || 0, assess: (pr.readings_completed || 0) > 0 ? 'In progress' : 'Not yet started' },
                { metric: 'Programme Lessons Completed', count: pr.lessons_completed || 0, assess: (pr.lessons_completed || 0) > 0 ? 'In progress' : 'Not yet started' },
                { metric: 'Self-Work Assignments Submitted', count: pr.self_work_completed || 0, assess: (pr.self_work_completed || 0) > 0 ? 'In progress' : 'Not yet started' },
                { metric: 'Personal History Assessment', count: `${historyProgress}%`, assess: historyComplete ? 'Complete' : 'Incomplete' },
              ].map((row, i) => (
                <tr key={i}>
                  <td className="border border-zinc-300 px-3 py-1">{row.metric}</td>
                  <td className="border border-zinc-300 px-3 py-1">{row.count}</td>
                  <td className="border border-zinc-300 px-3 py-1">{row.assess}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <P>{fullRef} {hasVerb} maintained {sobrietyDays} days of continuous sobriety as at the date of this report. {(pr.meetings_count || 0) > 0 ? `Meeting attendance of ${pr.meetings_count} session${(pr.meetings_count || 0) !== 1 ? 's' : ''} has been recorded and verified through the programme's digital attendance tracking system, which employs photo verification. ` : ''}{(pr.journals_count || 0) > 0 ? `${fullRef} ${hasVerb} completed ${pr.journals_count} daily journal entr${(pr.journals_count || 0) !== 1 ? 'ies' : 'y'}, demonstrating active engagement with the self-reflective components of the Recovery programme. ` : ''}</P>

          {/* 10. Case Management Plan */}
          {cp.plan_sections && Object.keys(cp.plan_sections).length > 0 && (
            <>
              <SectionTitle>{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '10' : '9'}. Case Management Plan</SectionTitle>
              <P>A comprehensive Case Management Plan has been developed for {fullRef} addressing the key areas of concern identified during {possessive} intake assessment. This plan was generated from {possessive} Personal History and has been reviewed by the participant{cp.user_edits && Object.keys(cp.user_edits).length > 0 ? ', who has made amendments to certain sections as noted below' : ''}. The following is a summary of the principal areas addressed:</P>
              {['crisis_reminder', 'sobriety', 'diet', 'meetings', 'counselling', 'housing', 'employment', 'health', 'social', 'legal', 'long_term'].map(key => {
                const content = cp.plan_sections[key];
                if (!content) return null;
                const titles = { crisis_reminder: 'Presenting Crisis Context', sobriety: 'Sobriety Maintenance', diet: 'Dietary & Nutritional Plan', meetings: 'Meeting Attendance & Sponsorship', counselling: 'Counselling & Psychiatric Care', housing: 'Housing & Accommodation', employment: 'Employment & Vocational', health: 'Physical Health & Wellbeing', social: 'Social & Interpersonal', legal: 'Legal Compliance', long_term: 'Long-Term Continuing Care' };
                const edited = cp.user_edits?.[key];
                return (
                  <div key={key} className="mb-3">
                    <p className="text-[10px] font-bold text-zinc-700 uppercase tracking-wider mb-1">
                      {titles[key] || key}{edited && <span className="text-amber-600 ml-1 normal-case font-medium italic"> (amended by participant {new Date(edited.edited_at).toLocaleDateString('en-AU')})</span>}
                    </p>
                    <p className="text-[10px] text-zinc-800 leading-[1.7] text-justify whitespace-pre-line">{content}</p>
                  </div>
                );
              })}
            </>
          )}

          {/* Programme Assessment */}
          <SectionTitle>{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '11' : '10'}. Programme Assessment & Observations</SectionTitle>
          <P>{fullRef} {hasVerb} been enrolled in the Axiom Recovery Programme since {enrolled} and {hasVerb} maintained {sobrietyDays} days of continuous sobriety as at the date of this report. The Axiom Recovery Programme is a structured evidence-based Recovery curriculum comprising a 32-week programme that starts with the 2-Week Window of Personal History, incorporating weekly compulsory readings, self-work assignments, daily reflective journaling, mental health monitoring and a minimum of five verified meeting attendances per week. The programme also provides a 24/7 AI-powered digital sponsor for crisis support and ongoing Recovery guidance.</P>
          <P>During {possessive} time in the programme, {fullRef} {hasVerb} {(pr.meetings_count || 0) > 10 ? 'demonstrated consistent engagement with' : (pr.meetings_count || 0) > 0 ? 'commenced engagement with' : 'not yet engaged with'} the meeting attendance requirements. {historyComplete ? `${Pronoun} ${hasVerb} completed the comprehensive Personal History assessment in its entirety, which includes sections on presenting crisis, substance use, living situation, employment, treatment history, mental health, physical health, social circumstances and legal matters.` : `The Personal History assessment is ${historyProgress}% complete.`} {cp.plan_sections && Object.keys(cp.plan_sections).length > 0 ? `A comprehensive Case Management Plan has been developed and is being actively implemented.` : ''}</P>
          <P>The programme employs multiple verification methods to ensure the integrity of the compliance data presented in this report: photo-verified meeting attendance with geo-location, timed reading sessions with minimum duration requirements, daily digital journal submissions, mental health check-in questionnaires and AI-monitored chat interactions. These measures are designed to provide the Court with confidence that the data presented herein reflects genuine programme participation rather than perfunctory compliance.</P>
          {(ph.crisis_using_for_court === 'yes' || ph.axiom_assist_court === 'yes') && (
            <P>It is respectfully submitted that this report be considered by the Court as evidence of {fullRef}'s voluntary engagement with a structured, evidence-based Recovery programme. The Axiom Recovery Programme does not advocate for or against any particular sentencing outcome. The purpose of this report is to provide a factual, verifiable and transparent account of {fullRef}'s efforts toward rehabilitation and sustained Recovery.</P>
          )}

          {/* Disclaimer */}
          <div className="bg-zinc-100 border border-zinc-300 rounded-md p-4 mt-6">
            <p className="text-[9px] text-zinc-600 leading-relaxed text-justify">
              <strong>DISCLAIMER:</strong> This report is generated from data provided by the programme participant through the Axiom Recovery Programme digital platform, a product of The Silkworth Institute. While the programme employs verification methods (photo verification with geo-location for meeting attendance, timed and logged reading sessions, daily digital journal submissions, mental health questionnaires and AI-monitored interactions), the accuracy of personal history and self-assessment data is dependent upon the participant's candour and honesty. This report is intended to supplement, not replace, professional clinical assessments conducted by qualified healthcare providers. All data is handled in accordance with applicable privacy legislation, including the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth) and where applicable, the Health Insurance Portability and Accountability Act (HIPAA). For questions regarding this report or the Axiom Recovery Programme, contact the Programme Administrator at The Silkworth Institute.
            </p>
          </div>

          {/* Signatures */}
          <div className="flex justify-between mt-10 pt-4">
            <div>
              <div className="w-48 border-t-2 border-zinc-900 mt-10 pt-1">
                <p className="text-[9px] text-zinc-700 font-semibold">Programme Administrator</p>
                <p className="text-[9px] text-zinc-500">The Silkworth Institute</p>
              </div>
              <p className="text-[9px] text-zinc-500 mt-1">Date: _______________</p>
            </div>
            <div>
              <div className="w-48 border-t-2 border-zinc-900 mt-10 pt-1">
                <p className="text-[9px] text-zinc-700 font-semibold">Participant Signature</p>
              </div>
              <p className="text-[9px] text-zinc-500 mt-1">Date: _______________</p>
            </div>
          </div>

          <div className="text-center mt-8 pt-3 border-t border-zinc-200">
            <p className="text-[8px] text-zinc-400">Axiom Recovery Programme: Participant Progress & Compliance Report</p>
            <p className="text-[8px] text-zinc-400">Prepared by The Silkworth Institute: Generated {today}</p>
            <p className="text-[8px] text-zinc-400">CONFIDENTIAL: FOR AUTHORISED COURT AND LEGAL USE ONLY</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompletionReport({ data, onClose }) {
  const ph = data?.personal_history || {};
  const ui = data?.user_info || {};
  const pr = data?.progress || {};
  const cp = data?.case_plan || {};
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const enrolled = ui.created_at ? new Date(ui.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';
  const sobrietyDays = ui.sobriety_days || 0;
  const weeks = Math.floor(sobrietyDays / 7);
  const months = Math.floor(sobrietyDays / 30);
  const pronoun = ph.gender === 'female' ? 'she' : ph.gender === 'male' ? 'he' : 'they';
  const possessive = ph.gender === 'female' ? 'her' : ph.gender === 'male' ? 'his' : 'their';
  const Pronoun = pronoun.charAt(0).toUpperCase() + pronoun.slice(1);
  const honorific = ph.gender === 'female' ? 'Ms' : ph.gender === 'male' ? 'Mr' : 'Mx';
  const lastName = (ui.name || '').split(' ').slice(-1)[0] || 'the participant';
  const fullRef = `${honorific} ${lastName}`;
  const P = (text) => <p className="text-[11px] text-zinc-800 leading-[1.7] mb-3 text-justify">{text}</p>;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto py-6 px-4" onClick={onClose}>
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-8" data-testid="completion-report">
          {/* Header */}
          <div className="text-center border-b-2 border-zinc-900 pb-5 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png" alt="Axiom" className="h-8 mx-auto mb-3" />
            <h1 className="text-lg font-black text-zinc-900 uppercase tracking-[0.2em]">Axiom Recovery Programme</h1>
            <h2 className="text-sm font-bold text-zinc-700 uppercase tracking-wider mt-1">Participant Progress & Compliance Report</h2>
            <p className="text-[10px] text-zinc-500 mt-2">Prepared by the Axiom Recovery Programme: A Product of The Silkworth Institute</p>
            <p className="text-[10px] text-zinc-500">Date of Report: {today}</p>
            <p className="text-[10px] text-red-600 font-bold mt-1">CONFIDENTIAL: FOR AUTHORISED USE ONLY</p>
          </div>

          {/* 1. Participant Identification */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">1. Participant Identification</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
              <div><span className="text-zinc-500">Full Name:</span> <span className="font-semibold">{ui.name || 'N/A'}</span></div>
              <div><span className="text-zinc-500">Date of Enrolment:</span> <span className="font-semibold">{enrolled}</span></div>
              <div><span className="text-zinc-500">Age Group:</span> <span className="font-semibold">{ph.age_group || 'N/A'}</span></div>
              <div><span className="text-zinc-500">Gender:</span> <span className="font-semibold capitalize">{ph.gender || 'N/A'}</span></div>
              <div><span className="text-zinc-500">Country of Birth:</span> <span className="font-semibold">{ph.country_of_birth || 'N/A'}</span></div>
              <div><span className="text-zinc-500">Sobriety/Clean Date:</span> <span className="font-semibold">{ui.sobriety_date ? new Date(ui.sobriety_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not set'}</span></div>
              <div><span className="text-zinc-500">Continuous Sobriety:</span> <span className="font-semibold">{sobrietyDays} days ({months > 0 ? `${months} month${months > 1 ? 's' : ''}, ` : ''}{weeks % 4} week{weeks % 4 !== 1 ? 's' : ''})</span></div>
              <div><span className="text-zinc-500">Programme Week:</span> <span className="font-semibold">{ui.current_week <= 0 ? 'Assessment Phase' : `Programme Week ${ui.current_week} of ${PROGRAMME_WEEK_COUNT}`}</span></div>
            </div>
          </div>

          {/* 2. Presenting Crisis */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">2. Presenting Crisis & Circumstances of Referral</h3>
            {ph.crisis_why_now ? P(`${fullRef} presented to the Axiom Recovery Programme on ${ui.sobriety_date || 'the date of enrolment'} under the following circumstances: ${ph.crisis_why_now}`) : P(`${fullRef} was referred to the programme for treatment of substance dependence.`)}
            {ph.crisis_what_happened && P(`In ${possessive} own account of the events leading to enrolment, ${fullRef} stated: "${ph.crisis_what_happened}"`)}
            {ph.crisis_heard_about && P(`${fullRef} was referred to the Axiom Recovery Programme via: ${ph.crisis_heard_about}.`)}
            {(ph.crisis_legal_issue === 'yes' || ph.crisis_using_for_court === 'yes') && P(`${fullRef} has disclosed that ${pronoun} ${pronoun === 'they' ? 'have' : 'has'} a current legal matter and has indicated ${possessive} intention to use participation in the Axiom Recovery Programme as evidence of rehabilitation for the purposes of sentencing submissions.`)}
          </div>

          {/* 3. Substance Use Profile */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">3. Substance Use Profile</h3>
            {P(`${fullRef}'s primary substance of dependence is ${ph.primary_drug || 'not specified'}${ph.secondary_drugs?.length ? `, with concurrent use of ${ph.secondary_drugs.join(', ')}` : ''}. ${Pronoun} has reported ${possessive} method of use as ${(ph.method_of_use || []).join(', ') || 'not specified'}. ${ph.ever_injected === 'yes' ? `${fullRef} has a history of intravenous drug use${ph.injected_last_3_months === 'yes' ? ', including within the past three months' : ''}.` : `${fullRef} denies any history of intravenous drug use.`}`)}
            {ph.additional_drug_info && P(`Additional substance use information: ${ph.additional_drug_info}`)}
          </div>

          {/* 4. Treatment History */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">4. Previous Treatment History</h3>
            {ph.has_previous_treatment === 'yes' && ph.treatment_entries?.length > 0 ? (
              <>
                {P(`${fullRef} reports ${ph.treatment_entries.length} previous treatment episode${ph.treatment_entries.length > 1 ? 's' : ''}:`)}
                {ph.treatment_entries.map((t, i) => (
                  <div key={i} className="ml-4 mb-2 border-l-2 border-zinc-200 pl-3">
                    <p className="text-[11px] text-zinc-800 leading-relaxed">
                      <strong>{i === 0 ? 'Most recent' : `Episode ${i + 1}`}:</strong> {t.type || 'Treatment'} at {t.location || 'N/A'} ({t.when || 'date not specified'}), duration {t.duration || 'not specified'}.{t.outcome ? ` Outcome: ${t.outcome}.` : ''}
                    </p>
                  </div>
                ))}
              </>
            ) : P(`${fullRef} reports no prior formal treatment for substance dependence.`)}
          </div>

          {/* 5. Mental Health */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">5. Mental Health</h3>
            {ph.has_mental_health_diagnosis === 'yes' ? (
              <>
                {P(`${fullRef} has disclosed a diagnosed mental health condition${ph.mh_admissions?.[0]?.diagnosis ? `: ${ph.mh_admissions[0].diagnosis}` : ''}. ${ph.currently_receiving_mh_treatment === 'yes' ? `${Pronoun} is currently receiving treatment${ph.mh_admissions?.[0]?.location ? ` from ${ph.mh_admissions[0].location}` : ''}.` : `${Pronoun} is not currently receiving treatment.`}`)}
                {ph.medication_details && P(`Current psychiatric medications: ${ph.medication_details}.`)}
              </>
            ) : P(`${fullRef} has not disclosed a formal mental health diagnosis.`)}
          </div>

          {/* 6. Health Concerns */}
          {(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes' || ph.diet_diabetic === 'yes' || ph.diet_food_allergies === 'yes') && (
            <div className="mb-6">
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">6. Significant Health Concerns</h3>
              {ph.has_major_health_issues === 'yes' && P(`Health issues: ${ph.health_issue_details || 'Reported but details not specified'}.`)}
              {ph.chronic_conditions === 'yes' && P(`Chronic conditions: ${ph.chronic_condition_details || 'Reported but details not specified'}.`)}
              {ph.medication_allergies === 'yes' && P(`Medication allergies: ${ph.medication_allergy_details || 'Present: details not specified'}. All treating practitioners have been advised.`)}
              {ph.diet_diabetic === 'yes' && P(`Diabetes: ${ph.diet_diabetic_type === 'type1' ? 'Type 1' : 'Type 2'}, managed via ${ph.diet_diabetic_management === 'insulin' ? 'insulin' : ph.diet_diabetic_management === 'dietary' ? 'dietary control' : 'tablets'}.`)}
              {ph.diet_food_allergies === 'yes' && P(`Food allergies: ${ph.diet_allergy_details || 'Present'}. Emergency protocol: ${ph.diet_allergy_action || 'seek immediate medical attention'}.`)}
            </div>
          )}

          {/* 7. Programme Compliance */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '7' : '6'}. Programme Compliance & Engagement</h3>
            {P(`Since enrolling on ${enrolled}, ${fullRef} has demonstrated the following level of engagement with the Axiom Recovery Programme:`)}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                { label: 'Meetings Attended', value: pr.meetings_count || 0 },
                { label: 'Journal Entries', value: pr.journals_count || 0 },
                { label: 'MH Check-ins', value: pr.mental_health_count || 0 },
                { label: 'Readings (30min+)', value: pr.readings_completed || 0 },
                { label: 'Lessons Completed', value: pr.lessons_completed || 0 },
                { label: 'Self-Work Tasks', value: pr.self_work_completed || 0 },
              ].map((s, i) => (
                <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-black text-zinc-900">{s.value}</p>
                  <p className="text-[8px] text-zinc-500 uppercase font-bold tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
            {P(`${fullRef} has maintained ${sobrietyDays} days of continuous sobriety/clean time as at the date of this report. ${(pr.meetings_count || 0) > 0 ? `Meeting attendance of ${pr.meetings_count} session${(pr.meetings_count || 0) !== 1 ? 's' : ''} has been recorded and verified through the programme's attendance tracking system. ` : ''}${(pr.journals_count || 0) > 0 ? `${fullRef} has submitted ${pr.journals_count} daily journal entr${(pr.journals_count || 0) !== 1 ? 'ies' : 'y'}, indicating active engagement with the self-reflective components of the programme. ` : ''}${(pr.mental_health_count || 0) > 0 ? `Mental health check-ins have been completed ${pr.mental_health_count} time${(pr.mental_health_count || 0) !== 1 ? 's' : ''}, demonstrating ongoing attention to psychological wellbeing.` : ''}`)}
          </div>

          {/* 8. Case Management Plan */}
          {cp.plan_sections && Object.keys(cp.plan_sections).length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '8' : '7'}. Case Management Plan</h3>
              {P(`A comprehensive Case Management Plan has been developed for ${fullRef} addressing the following areas of concern. This plan was generated from ${possessive} Personal History assessment and has been reviewed by the participant${cp.user_edits && Object.keys(cp.user_edits).length > 0 ? ', who has made amendments to certain sections as noted' : ''}.`)}
              {['crisis_reminder', 'sobriety', 'diet', 'meetings', 'counselling', 'housing', 'employment', 'health', 'social', 'legal', 'long_term'].map(key => {
                const content = cp.plan_sections[key];
                if (!content) return null;
                const titles = { crisis_reminder: 'Presenting Crisis', sobriety: 'Sobriety Maintenance', diet: 'Dietary Plan', meetings: 'Meeting Attendance & Sponsorship', counselling: 'Counselling & Mental Health', housing: 'Housing & Accommodation', employment: 'Employment & Career', health: 'Health & Physical Wellbeing', social: 'Social & Interpersonal', legal: 'Legal Matters', long_term: 'Long-Term Continuing Care' };
                const edited = cp.user_edits?.[key];
                return (
                  <div key={key} className="mb-3">
                    <p className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1">
                      {titles[key] || key}{edited && <span className="text-amber-600 ml-1 normal-case font-medium italic"> (amended by participant {new Date(edited.edited_at).toLocaleDateString()})</span>}
                    </p>
                    <p className="text-[11px] text-zinc-800 leading-[1.7] text-justify whitespace-pre-line">{content}</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legal Matters */}
          {(ph.has_legal_matters === 'yes' || ph.crisis_legal_issue === 'yes') && (
            <div className="mb-6">
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">{(ph.has_major_health_issues === 'yes' || ph.medication_allergies === 'yes') ? '9' : '8'}. Legal Matters</h3>
              {ph.charge_types?.length > 0 && P(`Current charges: ${ph.charge_types.join(', ')}. ${ph.legal_location ? `Court: ${ph.legal_location}.` : ''} ${ph.court_date_pending === 'yes' ? 'A court date is currently pending.' : ''}`)}
              {ph.previous_legal_history === 'yes' && ph.previous_charges?.length > 0 && (
                <>
                  {P(`${fullRef} has disclosed the following prior legal history:`)}
                  {ph.previous_charges.map((c, i) => (
                    <p key={i} className="text-[11px] text-zinc-700 leading-relaxed ml-4 mb-1">&bull; {c.when || 'Date unknown'}: {c.charge_type || 'Charge type not specified'}{c.details ? `: ${c.details}` : ''}</p>
                  ))}
                </>
              )}
              {ph.additional_legal_info && P(ph.additional_legal_info)}
            </div>
          )}

          {/* Programme Assessment */}
          <div className="mb-6">
            <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider mb-3 border-b border-zinc-300 pb-1">Programme Assessment & Observations</h3>
            {P(`${fullRef} has been enrolled in the Axiom Recovery Programme since ${enrolled} and has maintained ${sobrietyDays} days of continuous sobriety as at the date of this report. The programme is a structured evidence-based Recovery curriculum: a 32-week programme that starts with the 2-Week Window of Personal History, incorporating weekly readings, self-work assignments, daily journaling, mental health monitoring and a minimum of five verified meeting attendances per week.`)}
            {P(`During ${possessive} time in the programme, ${fullRef} has ${(pr.meetings_count || 0) > 10 ? 'demonstrated consistent engagement with' : (pr.meetings_count || 0) > 0 ? 'begun to engage with' : 'not yet engaged with'} the meeting attendance requirements. ${(pr.journals_count || 0) > 5 ? `${Pronoun} has shown willingness to engage in self-reflection through regular journaling. ` : ''}A comprehensive Case Management Plan has been developed addressing ${possessive} substance use, mental health, housing, employment, social environment, health, dietary needs and legal situation. ${cp.user_edits && Object.keys(cp.user_edits).length > 0 ? `${fullRef} has reviewed and amended certain sections of this plan, indicating active participation in ${possessive} own Recovery planning. ` : ''}This plan provides a structured framework for ${possessive} continued Recovery both within and beyond the programme.`)}
            {(ph.crisis_using_for_court === 'yes' || ph.axiom_assist_court === 'yes') && P(`It is noted that ${fullRef} has indicated ${possessive} intention to present ${possessive} programme participation to the Court. The Axiom Recovery Programme does not advocate for or against any particular sentencing outcome. This report is provided to offer the Court a factual and verifiable account of ${fullRef}'s engagement with a structured, evidence-based Recovery programme. The programme employs multiple verification methods including photo-verified meeting attendance, timed reading sessions and regular digital check-ins to ensure the integrity of compliance data presented herein.`)}
          </div>

          {/* Disclaimer */}
          <div className="bg-zinc-100 border border-zinc-300 rounded-md p-3 mb-6">
            <p className="text-[9px] text-zinc-600 leading-relaxed">
              <strong>DISCLAIMER:</strong> This report is generated from data provided by the programme participant through the Axiom Recovery Programme digital platform. While the programme employs verification methods (photo verification for meeting attendance, timed readings, digital check-ins and AI-monitored interactions), the accuracy of personal history and self-assessment data is dependent upon the participant's candour and honesty. This report is intended to supplement, not replace, professional clinical assessments conducted by qualified healthcare providers. All data is handled in accordance with applicable privacy legislation, including the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth), the Health Insurance Portability and Accountability Act (HIPAA) where applicable and the programme's privacy policy. Axiom Recovery Programme is a product of The Silkworth Institute.
            </p>
          </div>

          {/* Signatures */}
          <div className="flex justify-between pt-4">
            <div>
              <div className="w-48 border-t-2 border-zinc-900 mt-10 pt-1">
                <p className="text-[9px] text-zinc-700 font-semibold">Programme Administrator</p>
                <p className="text-[9px] text-zinc-500">The Silkworth Institute</p>
              </div>
              <p className="text-[9px] text-zinc-500 mt-1">Date: _______________</p>
            </div>
            <div>
              <div className="w-48 border-t-2 border-zinc-900 mt-10 pt-1">
                <p className="text-[9px] text-zinc-700 font-semibold">Participant</p>
              </div>
              <p className="text-[9px] text-zinc-500 mt-1">Date: _______________</p>
            </div>
          </div>

          <div className="text-center mt-8 pt-3 border-t border-zinc-200">
            <p className="text-[8px] text-zinc-400">Axiom Recovery Programme: Participant Progress and Compliance Report</p>
            <p className="text-[8px] text-zinc-400">Generated {today}. CONFIDENTIAL: FOR AUTHORISED USE ONLY</p>
          </div>
        </div>

        <div className="p-4 border-t border-zinc-200">
          <Button onClick={onClose} variant="outline" className="w-full h-10" data-testid="close-completion-report">Close Report</Button>
        </div>
      </div>
    </div>
  );
}

function LongTermPlanReport({ data, onClose }) {
  const ph = data?.personal_history || {};
  const ui = data?.user_info || {};
  const cp = data?.case_plan || {};
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const longTermSections = [
    {
      title: 'Sobriety Maintenance',
      color: 'bg-blue-50 border-blue-200',
      content: `Continue complete abstinence from ${ph.primary_drug || 'all substances'}. Recognise that the Disease is chronic and progressive: Recovery requires daily commitment. If relapse occurs, re-engage immediately with your sponsor and attend meetings the same day.`
    },
    {
      title: 'Meeting Attendance & Sponsorship',
      color: 'bg-green-50 border-green-200',
      content: `Maintain a minimum of 3-5 meetings per week. Transition from being sponsored to also sponsoring others: carrying the message is one of the most powerful tools in Recovery. Consider service work at your home group.`
    },
    {
      title: 'Counselling & Mental Health',
      color: 'bg-violet-50 border-violet-200',
      content: ph.has_mental_health_diagnosis === 'yes'
        ? `Continue all mental health treatment. ${ph.medication_details ? `Current medications: ${ph.medication_details}. ` : ''}Regular psychiatric review is essential. Your mental health and your addiction are interconnected: neglecting one risks the other.`
        : 'Consider periodic counselling even without a formal diagnosis. Life in Recovery presents new challenges: relationships, employment, self-identity: that benefit from professional support.'
    },
    {
      title: 'Housing & Stability',
      color: 'bg-sky-50 border-sky-200',
      content: ph.living_arrangement?.includes('Homeless')
        ? 'PRIORITY: Secure stable long-term housing. Contact local housing support services, halfway houses or sober living environments. Stable accommodation is one of the strongest predictors of sustained Recovery.'
        : `Maintain stable housing. ${ph.living_how_long ? `You have been at your current accommodation for ${ph.living_how_long}.` : ''} If your living situation changes, prioritise a Recovery-safe environment.`
    },
    {
      title: 'Employment & Career Development',
      color: 'bg-amber-50 border-amber-200',
      content: ph.work_environment_dangerous === 'yes'
        ? `Your work environment has been identified as a risk to your Recovery. Plan a transition to a safer role or industry. ${ph.profession ? `Your skills in ${ph.profession}` : 'Your existing skills'} may be transferable to a Recovery-compatible environment.`
        : ph.is_employed === 'yes'
          ? `Continue current employment. ${ph.profession ? `Your role as ${ph.profession}` : 'Your position'} provides structure and financial stability: both critical to Recovery. Consider professional development to build long-term career security.`
          : 'Develop employment readiness: update your CV, explore vocational training and consider volunteer work. Start with part-time or flexible work if full-time feels overwhelming.'
    },
    {
      title: 'Diet & Physical Health',
      color: 'bg-rose-50 border-rose-200',
      content: [
        'Maintain regular meals (breakfast, lunch, dinner).',
        ph.diet_food_allergies === 'yes' ? `Continue managing food allergies: ${ph.diet_allergy_details || 'as identified'}.` : null,
        ph.diet_diabetic === 'yes' ? `Manage diabetes through ${ph.diet_diabetic_management || 'your treatment plan'}.` : null,
        'Exercise regularly: aim for 30-60 minutes daily. Walking, swimming or gym work all support Recovery.',
        'Reduce caffeine and sugar. Stay hydrated. Your body is recovering alongside your mind.'
      ].filter(Boolean).join(' ')
    },
    {
      title: 'Social & Relationships',
      color: 'bg-teal-50 border-teal-200',
      content: ph.friends_use_drugs === 'yes'
        ? 'Reassess your social circle. Distance yourself from people who actively use drugs or alcohol. Build new friendships through meetings, Recovery events and sober activities. This is one of the hardest but most important changes you will make.'
        : 'Maintain healthy relationships and continue building your Recovery community. Attend social events at meetings, join service committees and invest in friendships that support your sobriety.'
    },
    {
      title: 'Legal Compliance',
      color: 'bg-zinc-50 border-zinc-300',
      content: (ph.has_legal_matters === 'yes' || ph.crisis_legal_issue === 'yes')
        ? 'Maintain full compliance with all court orders, probation conditions and legal obligations. Keep your legal team informed of your programme progress. Your continued sobriety and programme engagement are your strongest evidence of genuine change.'
        : 'No current legal matters recorded. Maintain lawful conduct: legal issues are one of the most common consequences of relapse.'
    },
    {
      title: 'Ongoing Self-Assessment',
      color: 'bg-indigo-50 border-indigo-200',
      content: 'Review this Long-Term Plan every 3 months. Honest self-assessment is the foundation of sustained Recovery. Ask yourself: Am I attending enough meetings? Am I being honest with my sponsor? Am I taking care of my physical and mental health? Am I at risk of complacency?'
    },
    {
      title: 'Remember Your Crisis',
      color: 'bg-red-50 border-red-200',
      content: ph.crisis_why_now
        ? `You came to Axiom Recovery Programme because: "${ph.crisis_why_now.substring(0, 250)}${ph.crisis_why_now.length > 250 ? '...' : ''}"\n\nNever forget this. The Disease will try to convince you that things were not that bad, that you can control it, that you are different now. You are different: because you are in Recovery. Stay here.`
        : 'Remember why you started this journey. The Disease will try to minimise your past. Do not let it.'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto py-6 px-4" onClick={onClose}>
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-8" data-testid="longterm-plan-report">
          {/* Header */}
          <div className="text-center border-b border-zinc-200 pb-6 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png" alt="Axiom" className="h-8 mx-auto mb-3" />
            <h1 className="text-xl font-black text-zinc-900 uppercase tracking-wider">Long-Term Recovery Plan</h1>
            <p className="text-xs text-zinc-500 mt-1">Beyond the 2-Week Window + 30-Week Programme</p>
            <p className="text-xs text-zinc-400">{ui.name}, {today}</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs text-blue-800 leading-relaxed">
              This plan is designed to support your continued Recovery after completing the Axiom Recovery Programme. 
              It is based on your personal history, case management plan and programme progress. 
              Review this plan regularly with your sponsor, counsellor or support network.
            </p>
          </div>

          <div className="space-y-4">
            {longTermSections.map((section, i) => (
              <div key={i} className={`border rounded-xl p-4 ${section.color}`}>
                <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider mb-2">{i + 1}. {section.title}</h3>
                <p className="text-[11px] text-zinc-700 leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 pt-4 border-t border-zinc-200">
            <p className="text-[8px] text-zinc-400">Axiom Recovery Programme: Long-Term Recovery Plan</p>
            <p className="text-[8px] text-zinc-400">Generated {today}. CONFIDENTIAL</p>
          </div>
        </div>

        <div className="p-4 border-t border-zinc-200">
          <Button onClick={onClose} variant="outline" className="w-full h-10" data-testid="close-longterm-plan">Close Plan</Button>
        </div>
      </div>
    </div>
  );
}

const stickmanFields = ['health_diet', 'job_finances', 'friends', 'home', 'relationships', 'family'];

function CaseManagementPage() {
  useAllowPrint();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showReport, setShowReport] = useState(false);
  const [showCompletionReport, setShowCompletionReport] = useState(false);
  const [showLongTermPlan, setShowLongTermPlan] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/case-management`);
      if (response.ok) {
        setData(await response.json());
      }
    } catch (error) {
      console.error('Error fetching case management data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="text-zinc-500 text-sm">Loading case management data...</div>
      </div>
    );
  }

  const ph = data?.personal_history || {};
  const sd = data?.stickman || {};
  const ui = data?.user_info || {};
  const pr = data?.progress || {};
  const cp = data?.case_plan || {};

  const historyFields = ['gender', 'age_group', 'primary_drug', 'method_of_use', 'ever_injected', 'living_arrangement', 'has_previous_treatment', 'has_mental_health_diagnosis', 'crisis_why_now', 'crisis_last_drink_date', 'has_legal_matters'];
  const filledCount = historyFields.filter(f => ph[f]).length;
  const historyProgress = Math.round((filledCount / historyFields.length) * 100);
  const historyComplete = historyProgress === 100;

  const stickmanFields = ['health_diet', 'job_finances', 'friends', 'home', 'relationships', 'family'];
  const stickmanFilled = stickmanFields.filter(f => sd[f]).length;
  const stickmanComplete = stickmanFilled === stickmanFields.length;

  const programmeComplete = (ui.current_week || 0) >= 30;
  const sobrietyDays = ui.sobriety_days || 0;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'reports', label: 'Reports' },
    { id: 'stickman', label: 'Profile' },
    { id: 'demographics', label: 'Demographics' },
    { id: 'substance', label: 'Substance Use' },
    { id: 'legal', label: 'Legal / CJS' },
    { id: 'treatment', label: 'Treatment' },
  ];

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <PageHeader title="Case Management" />

      {/* Court Report Overlay */}
      {showReport && (
        <CourtReport 
          data={data} 
          onClose={() => setShowReport(false)}
          historyProgress={historyProgress}
          historyComplete={historyComplete}
          stickmanFilled={stickmanFilled}
          stickmanComplete={stickmanComplete}
        />
      )}

      {/* Completion Report Overlay */}
      {showCompletionReport && (
        <CompletionReport data={data} onClose={() => setShowCompletionReport(false)} />
      )}

      {/* Long-Term Plan Overlay */}
      {showLongTermPlan && (
        <LongTermPlanReport data={data} onClose={() => setShowLongTermPlan(false)} />
      )}

      <main className="pt-20 pb-6 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="mb-4">
            <h1 className="text-lg font-bold text-zinc-950" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Case Management Profile
            </h1>
            <p className="text-xs text-zinc-500">Criminal justice reporting, treatment planning and programme research</p>
          </div>

          {/* Generate Court Report button */}
          <Button
            onClick={() => setShowReport(true)}
            className="w-full mb-4 bg-zinc-900 hover:bg-zinc-800 text-white h-12 font-semibold"
            data-testid="generate-court-report-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generate Court Report
          </Button>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4" data-testid="cjs-disclaimer">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-800 leading-relaxed">
                <strong>Notice:</strong> This profile may be used for criminal justice sentencing reports, programme integrity assessment and long-term qualitative/quantitative research. All data is encrypted and handled per HIPAA/privacy regulations.
              </p>
            </div>
          </div>

          {/* Tab selector */}
          <div className="flex gap-1 overflow-x-auto bg-white border border-zinc-200 rounded-xl p-1 mb-4 no-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex-shrink-0 ${
                  activeTab === tab.id ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-3">
              {/* Client Info Card */}
              <Section id="client-info" icon={User} title="Client Information" defaultOpen color="blue" testId="section-client-info">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <DataRow label="Name" value={ui.name} />
                  <DataRow label="Email" value={ui.email} />
                  <DataRow label="Programme Week" value={ui.current_week <= 0 ? '2-Week Window' : `Programme Week ${ui.current_week} of ${PROGRAMME_WEEK_COUNT}`} />
                  <DataRow label="Sobriety Date" value={ui.sobriety_date || 'Not set'} />
                  <DataRow label="Drug of Choice" value={ui.drug_of_choice || ph.primary_drug || 'Not set'} />
                  <DataRow label="Enrolled" value={ui.created_at ? new Date(ui.created_at).toLocaleDateString() : 'N/A'} />
                </div>
              </Section>

              {/* Assessment Completion */}
              <Section id="assessment" icon={FileText} title="Assessment Completion" defaultOpen color="amber" testId="section-assessment">
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-700">Personal History</span>
                      <StatusBadge completed={historyComplete} label={historyComplete ? 'Complete' : `${historyProgress}%`} />
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${historyComplete ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${historyProgress}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-zinc-700">Stickman Assessment</span>
                      <StatusBadge completed={stickmanComplete} label={stickmanComplete ? 'Complete' : `${stickmanFilled}/6`} />
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${stickmanComplete ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${(stickmanFilled / 6) * 100}%` }} />
                    </div>
                  </div>
                </div>
              </Section>

              {/* Programme Progress */}
              <Section id="programme-progress" icon={BarChart3} title="Programme Progress" color="green" testId="section-progress">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{pr.journals_count || 0}</p>
                    <p className="text-[10px] text-blue-600 uppercase font-medium">Journal Entries</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{pr.mental_health_count || 0}</p>
                    <p className="text-[10px] text-green-600 uppercase font-medium">MH Check-ins</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{pr.meetings_count || 0}</p>
                    <p className="text-[10px] text-purple-600 uppercase font-medium">Meetings Logged</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-700">{pr.lessons_completed || 0}</p>
                    <p className="text-[10px] text-amber-600 uppercase font-medium">Lessons Done</p>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* REPORTS TAB */}
          {activeTab === 'reports' && (
            <div className="space-y-4">
              {/* Court Sentencing Report */}
              <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="report-court">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <Gavel className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-zinc-900">Court Sentencing Report</h3>
                    <p className="text-[11px] text-zinc-500">Formal report for Criminal Justice System use</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mb-3">Generates a comprehensive report including your personal history, programme compliance, substance use profile, treatment goals and case management plan. Suitable for submission to the Court.</p>
                <Button onClick={() => setShowReport(true)} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10 text-xs font-bold uppercase tracking-wider" data-testid="generate-court-report-btn">
                  <FileText className="w-4 h-4 mr-2" /> Generate Court Report
                </Button>
              </div>

              {/* Programme Completion Report */}
              <div className={`bg-white border rounded-xl p-4 shadow-sm ${programmeComplete ? 'border-green-300' : 'border-zinc-200'}`} data-testid="report-completion">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${programmeComplete ? 'bg-green-50' : 'bg-zinc-100'}`}>
                    <CheckCircle className={`w-5 h-5 ${programmeComplete ? 'text-green-600' : 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-zinc-900">Programme Completion Report</h3>
                    <p className="text-[11px] text-zinc-500">
                      {programmeComplete ? 'Your programme is complete: generate your report' : `Available at Week 30+ (currently Week ${ui.current_week || 'Assessment'})`}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mb-3">A detailed overview of your entire Recovery journey: the 32-week programme that starts with the 2-Week Window: sobriety milestones, compliance metrics, personal growth and recommendations. This can also serve as the foundation for a court sentencing report.</p>
                <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div><span className="text-zinc-500">Current Week:</span> <span className="font-semibold text-zinc-800">{ui.current_week <= 0 ? 'Assessment' : `Programme Week ${ui.current_week}`}</span></div>
                    <div><span className="text-zinc-500">Sobriety:</span> <span className="font-semibold text-zinc-800">{sobrietyDays} days</span></div>
                    <div><span className="text-zinc-500">Meetings:</span> <span className="font-semibold text-zinc-800">{pr.meetings_count || 0} total</span></div>
                    <div><span className="text-zinc-500">Journals:</span> <span className="font-semibold text-zinc-800">{pr.journals_count || 0} total</span></div>
                    <div><span className="text-zinc-500">Readings:</span> <span className="font-semibold text-zinc-800">{pr.readings_completed || 0} completed</span></div>
                    <div><span className="text-zinc-500">Self-Work:</span> <span className="font-semibold text-zinc-800">{pr.self_work_completed || 0} submitted</span></div>
                  </div>
                </div>
                <Button
                  onClick={() => setShowCompletionReport(true)}
                  disabled={!programmeComplete && ui.role !== 'superadmin'}
                  className={`w-full h-10 text-xs font-bold uppercase tracking-wider ${programmeComplete || ui.role === 'superadmin' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'}`}
                  data-testid="generate-completion-report-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> {programmeComplete ? 'Generate Completion Report' : ui.role === 'superadmin' ? 'Generate (Admin Preview)' : 'Available at Week 30'}
                </Button>
              </div>

              {/* Long-Term Recovery Plan */}
              <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm" data-testid="report-longterm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-zinc-900">Long-Term Recovery Plan</h3>
                    <p className="text-[11px] text-zinc-500">Your roadmap beyond the 32-week programme</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mb-3">Based on your Case Management Plan and programme progress, this report outlines recommended actions for sustained long-term Recovery: continued meeting attendance, sponsorship, counselling, employment, housing and lifestyle goals.</p>
                <Button
                  onClick={() => setShowLongTermPlan(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-xs font-bold uppercase tracking-wider"
                  data-testid="generate-longterm-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> View Long-Term Plan
                </Button>
              </div>
            </div>
          )}

          {/* STICKMAN TAB */}
          {activeTab === 'stickman' && (
            <div className="space-y-3">
              <InteractiveStickman 
                personalHistory={ph} 
                stickmanData={sd} 
                userName={sd.name_picture || ui.name}
                onNavigate={navigate}
              />
            </div>
          )}

          {/* DEMOGRAPHICS TAB */}
          {activeTab === 'demographics' && (
            <div className="space-y-3">
              <Section id="demo" icon={User} title="Demographics" defaultOpen color="blue" testId="section-demographics">
                <div className="space-y-1">
                  <DataRow label="Gender" value={ph.gender} />
                  <DataRow label="Age Group" value={ph.age_group} />
                  <DataRow label="Country of Birth" value={ph.country_of_birth} />
                  <DataRow label="Language at Home" value={ph.language_at_home} />
                  <DataRow label="Indigenous Status" value={ph.indigenous_status} />
                  {ph.indigenous_status === 'yes' && <DataRow label="Indigenous Details" value={ph.indigenous_details} />}
                </div>
              </Section>

              <Section id="living" icon={Home} title="Living Situation" color="green" testId="section-living">
                <div className="space-y-1">
                  <DataRow label="Living Arrangement" value={ph.living_arrangement} />
                  <DataRow label="Usual Accommodation" value={ph.usual_accommodation} />
                  <DataRow label="Homeless Details" value={ph.homeless_details} />
                </div>
              </Section>

              <Section id="employment" icon={Briefcase} title="Income & Employment" color="amber" testId="section-employment">
                <div className="space-y-1">
                  <DataRow label="Currently Employed" value={ph.is_employed} />
                  <DataRow label="Previously Employed" value={ph.has_previous_employment} />
                  <DataRow label="Employer" value={ph.employer} />
                  <DataRow label="Profession" value={ph.profession} />
                  <DataRow label="Industry" value={ph.industry} />
                  <DataRow label="Employment Duration" value={ph.employment_duration} />
                  <DataRow label="Income Source" value={ph.income_source} />
                  <DataRow label="Income Details" value={ph.income_details} />
                </div>
                {ph.employment_history?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-bold text-zinc-700 uppercase mb-1">Employment History</p>
                    {ph.employment_history.map((job, i) => (
                      <div key={i} className="bg-zinc-50 rounded-md p-2 mb-1 text-[10px]">
                        <span className="font-medium">{job.employer || 'N/A'}</span> - {job.profession || 'N/A'} ({job.duration || 'N/A'})
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          )}

          {/* SUBSTANCE USE TAB */}
          {activeTab === 'substance' && (
            <div className="space-y-3">
              <Section id="primary" icon={Pill} title="Substance Use Profile" defaultOpen color="red" testId="section-substance">
                <div className="space-y-1">
                  <DataRow label="Primary Drug" value={ph.primary_drug} />
                  <DataRow label="Secondary Drugs" value={ph.secondary_drugs?.join(', ')} />
                  <DataRow label="Method of Use" value={ph.method_of_use?.join(', ')} />
                  <DataRow label="Ever Injected" value={ph.ever_injected} />
                  <DataRow label="Sharing Equipment" value={ph.sharing_equipment} />
                  <DataRow label="Drug Info" value={ph.additional_drug_info} />
                </div>
              </Section>

              <Section id="treatment-history" icon={Activity} title="Previous Treatment" color="purple" testId="section-treatment-history">
                <div className="space-y-1">
                  <DataRow label="Previous Treatment" value={ph.has_previous_treatment || ph.previous_treatment} />
                  <DataRow label="Treatment Type" value={ph.treatment_type} />
                  <DataRow label="Treatment Details" value={ph.treatment_details} />
                  <DataRow label="Previous Treatment Count" value={ph.previous_treatment_count} />
                </div>
              </Section>

              <Section id="mental-health" icon={Brain} title="Mental Health" color="indigo" testId="section-mental-health">
                <div className="space-y-1">
                  <DataRow label="MH Diagnosis" value={ph.has_mental_health_diagnosis} />
                  <DataRow label="Diagnosis Details" value={ph.mental_health_details} />
                  <DataRow label="On Medication" value={ph.on_medication} />
                  <DataRow label="Medication Details" value={ph.medication_details} />
                  <DataRow label="Additional MH Info" value={ph.additional_mental_health_info} />
                </div>
              </Section>
            </div>
          )}

          {/* LEGAL / CJS TAB */}
          {activeTab === 'legal' && (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-2" data-testid="cjs-notice">
                <div className="flex items-start gap-2">
                  <Gavel className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-[10px] text-red-800">
                    <p className="font-bold mb-1">Criminal Justice System Report</p>
                    <p>This section is used for court sentencing, probation and parole reporting. All information is verified and may be shared with authorised justice personnel.</p>
                  </div>
                </div>
              </div>

              <Section id="legal-matters" icon={Gavel} title="Legal Matters" defaultOpen color="red" testId="section-legal">
                <div className="space-y-1">
                  <DataRow label="Currently on Orders" value={ph.currently_on_orders} />
                  <DataRow label="Legal Matters" value={ph.has_legal_matters} />
                  <DataRow label="Charge Types" value={ph.charge_types?.join(', ')} />
                  <DataRow label="Court Dates" value={ph.court_dates} />
                  <DataRow label="Previous Legal History" value={ph.previous_legal_history} />
                  <DataRow label="Previous Legal Details" value={ph.previous_legal_details} />
                  <DataRow label="Additional Legal Info" value={ph.additional_legal_info} />
                </div>
              </Section>

              <Section id="compliance" icon={Shield} title="Programme Compliance" color="green" testId="section-compliance">
                <div className="space-y-1">
                  <DataRow label="Current Week" value={ui.current_week <= 0 ? '2-Week Window' : (isTwoWeekWindow(ui.current_week) ? `2-Week Window: Week ${ui.current_week}` : `Programme Week ${programmeWeekNumber(ui.current_week)}`)} />
                  <DataRow label="Journal Entries" value={`${pr.journals_count || 0} total`} />
                  <DataRow label="MH Check-ins" value={`${pr.mental_health_count || 0} total`} />
                  <DataRow label="Meetings Logged" value={`${pr.meetings_count || 0} total`} />
                  <DataRow label="Lessons Completed" value={`${pr.lessons_completed || 0} total`} />
                  <DataRow label="Personal History" value={historyComplete ? 'Complete' : `${historyProgress}% complete`} />
                  <DataRow label="Stickman Assessment" value={stickmanComplete ? 'Complete' : `${stickmanFilled}/6 areas`} />
                </div>
              </Section>

              <Section id="research" icon={BarChart3} title="Research Data" color="indigo" testId="section-research">
                <p className="text-xs text-zinc-600 mb-2">
                  Anonymous data from this profile contributes to long-term qualitative and quantitative research on programme integrity and effectiveness.
                </p>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                  <p className="text-[10px] text-indigo-700">
                    <strong>Research Consent:</strong> By participating in the Axiom Recovery Programme, your anonymised data may be used for academic research studies evaluating treatment outcomes, programme effectiveness and Recovery patterns. No personally identifiable information is shared with researchers.
                  </p>
                </div>
              </Section>
            </div>
          )}

          {/* TREATMENT TAB */}
          {activeTab === 'treatment' && (
            <div className="space-y-3">
              <Section id="treatment-plan" icon={Target} title="Treatment Goals" defaultOpen color="purple" testId="section-treatment-goals">
                <p className="text-xs text-zinc-600 mb-3">
                  Treatment goals are derived from your assessments and adapted as you progress.
                </p>
                <div className="space-y-2">
                  {/* Auto-generated goals from history */}
                  {ph.primary_drug && (
                    <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                      <p className="text-[10px] font-bold text-red-800 uppercase mb-1">Primary Substance Goal</p>
                      <p className="text-xs text-red-700">Achieve and maintain abstinence from {ph.primary_drug}</p>
                    </div>
                  )}
                  {ph.has_mental_health_diagnosis === 'yes' && (
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                      <p className="text-[10px] font-bold text-indigo-800 uppercase mb-1">Mental Health Goal</p>
                      <p className="text-xs text-indigo-700">Continue mental health management and engage with support services</p>
                    </div>
                  )}
                  {ph.is_employed === 'no' && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                      <p className="text-[10px] font-bold text-amber-800 uppercase mb-1">Employment Goal</p>
                      <p className="text-xs text-amber-700">Develop employment readiness and explore job training opportunities</p>
                    </div>
                  )}
                  {(ph.has_legal_matters === 'yes' || ph.currently_on_orders === 'yes') && (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                      <p className="text-[10px] font-bold text-blue-800 uppercase mb-1">Legal Compliance Goal</p>
                      <p className="text-xs text-blue-700">Maintain full compliance with legal orders and programme requirements</p>
                    </div>
                  )}
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-[10px] font-bold text-green-800 uppercase mb-1">Programme Goal</p>
                    <p className="text-xs text-green-700">Complete the 32-week Axiom Recovery Programme with minimum 5 meetings, 5 journals and 5 MH check-ins per week</p>
                  </div>
                </div>
              </Section>

              <Section id="milestones" icon={CheckCircle} title="Milestones" color="green" testId="section-milestones">
                <div className="space-y-2">
                  {[
                    { label: 'Enrolled in Programme', done: true },
                    { label: 'Personal History Complete', done: historyComplete },
                    { label: 'Stickman Assessment Complete', done: stickmanComplete },
                    { label: 'First Journal Entry', done: (pr.journals_count || 0) > 0 },
                    { label: 'First Meeting Logged', done: (pr.meetings_count || 0) > 0 },
                    { label: '2-Week Window Complete', done: (ui.current_week || 0) > 0 },
                    { label: 'Week 4 Reached (Group Chat)', done: (ui.current_week || 0) >= 4 },
                    { label: 'Week 15 (Halfway)', done: (ui.current_week || 0) >= 15 },
                    { label: 'Programme Complete (Week 30)', done: (ui.current_week || 0) >= 30 },
                  ].map((milestone, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {milestone.done 
                        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        : <div className="w-4 h-4 rounded-full border-2 border-zinc-300 flex-shrink-0" />
                      }
                      <span className={`text-xs ${milestone.done ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`}>
                        {milestone.label}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}

          {/* Footer actions */}
          <div className="mt-6 space-y-2">
            <Button
              onClick={() => navigate('/personal-history')}
              variant="outline"
              className="w-full h-11"
              data-testid="update-history-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Update Personal History
            </Button>
            <Button
              onClick={() => navigate('/self-work/stickman')}
              variant="outline"
              className="w-full h-11"
              data-testid="update-stickman-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              Update Stickman Assessment
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default CaseManagementPage;
